import React, {
  memo,
  type ReactElement,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  ButtonSelect,
  MenuMulti,
  type MenuOption,
} from '@saviynt/design-system';

import { ADVANCED_FILTERS_METHODS } from '../../constants';
import {
  useAsyncDebounce,
  useInvertedLocalize,
  useOutsideClick,
} from '../../hooks';
import { createDevRejectionHandler } from '../../utils';
import { OUTSIDE_CLICK_SELECTORS } from '../../utils/clickOutsideUtils';

import { type SodAdvancedFiltersSharedFunc } from './SodControls';

interface FilterRefType extends HTMLDivElement {
  openDropdown: () => void;
  closeDropdown?: () => void;
}

const CHIP_FILTER_IGNORE_SELECTORS = [
  OUTSIDE_CLICK_SELECTORS.menuMultiDropdown,
];

/**
 * Interface for ChipFilter component props
 */
interface ChipFilterProps
  extends Omit<SodAdvancedFiltersSharedFunc, 'handleFilterChange'> {
  value: string;
  label: string;
  method: string;
  options: MenuOption[];
  activeOptions: MenuOption[];
  enabledFilterValue?: string | null;
  setEnabledFilterValue?: (value: string | null) => void;
  searchDebounce: number;
  loadingText: string;
  noDataText: string | undefined;
  footerLink1Text: string;
  footerLink2Text: string;
  filterSearchError?: string;
  onFooterLink1Click: SodAdvancedFiltersSharedFunc['handleFilterChange'];
  isDisabled?: boolean;
  minOptionsForSearch?: number;
}

/**
 * ChipFilter component provides a filterable dropdown with multi-select capabilities.
 * It supports different filtering methods (API, SEARCH, FIXED) and displays selected options as chips.
 *
 * @component
 * @param {Object} props - The component props
 * @param {string} props.value - Unique identifier for the filter
 * @param {string} props.label - Display label for the filter dropdown
 * @param {string} props.method - Filtering method ('API', 'SEARCH', or 'FIXED')
 * @param {MenuOption[]} [props.options=[]] - Array of available options for the filter
 * @param {MenuOption[]} [props.activeOptions=[]] - Array of selected options
 * @param {Function} props.handleFilterSearch - Callback function for handling filter search - return undefined if error, return false or [] if no options
 * @param {number} props.searchDebounce - Debounce delay in milliseconds for search functionality
 * @param {string} props.loadingText - Text to display during loading state
 * @param {string} [props.noDataText] - Text to display when no data is available
 * @param {string} props.footerLink1Text - Text for the first footer link
 * @param {string} props.footerLink2Text - Text for the second footer link
 * @param {string} props.filterSearchError - In case of filter search error text
 * @param {Function} props.handleOptionsClick - Callback for when options are selected/deselected
 * @param {Function} props.onFooterLink1Click - Callback for footer link click action
 * @param {boolean} props.isDisabled - Flag to disable the filter
 *
 * @example
 * ```tsx
 * <ChipFilter
 *   value="status"
 *   label="Status"
 *   method="API"
 *   options={[
 *     { value: 'active', label: 'Active', isChecked: false },
 *     { value: 'inactive', label: 'Inactive', isChecked: false }
 *   ]}
 *   activeOptions={[
 *     { value: 'active', label: 'Active', isChecked: true }
 *   ]}
 *   handleFilterSearch={async (key, searchString) => {}}
 *   searchDebounce={300}
 *   loadingText="Loading..."
 *   noDataText="No results found"
 *   footerLink1Text="Remove Filter"
 *   footerLink2Text="Clear Selection"
 *   handleOptionsClick={(value, method, selectedOptions) => {}}
 *   onFooterLink1Click={(data) => {}}
 * />
 * ```
 *
 * @returns {ReactElement} A dropdown menu component with multi-select and filtering capabilities
 */
function ChipFilter({
  value,
  label,
  method = ADVANCED_FILTERS_METHODS.API,
  options = [],
  activeOptions = [],
  handleFilterSearch,
  searchDebounce,
  enabledFilterValue,
  setEnabledFilterValue,
  noDataText,
  loadingText,
  footerLink1Text,
  footerLink2Text,
  filterSearchError = '',
  handleOptionsClick,
  onFooterLink1Click,
  isDisabled,
  minOptionsForSearch,
}: ChipFilterProps): ReactElement {
  const l = useInvertedLocalize();
  const [searchText, setSearchText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState<MenuOption[]>(options);
  const [isError, setIsError] = useState(false);

  const filterRef = useRef<FilterRefType | null>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);
  const [isMenuExpanded, setIsMenuExpanded] = useState(false);

  const PROGRAMMATIC_OPEN_DELAY_MS = 200;

  // Programmatic open when parent requests (e.g. from breadcrumb)
  useEffect(() => {
    const shouldOpen = enabledFilterValue && value === enabledFilterValue;

    if (!shouldOpen) {
      return undefined;
    }

    // Delay opening slightly so the container transition is smooth.
    const timeoutId = window.setTimeout(() => {
      filterRef.current?.openDropdown();
      setEnabledFilterValue?.(null);
    }, PROGRAMMATIC_OPEN_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [value, enabledFilterValue, setEnabledFilterValue]);

  useOutsideClick({
    isEnabled: isMenuExpanded,
    containerRef: menuContainerRef,
    ignoreSelectors: CHIP_FILTER_IGNORE_SELECTORS,
    onOutsideClick: () => {
      setIsMenuExpanded(false);

      // Prefer ref method if provided by DS; otherwise click the trigger to toggle-close.
      if (filterRef.current?.closeDropdown) {
        filterRef.current.closeDropdown();

        return;
      }

      const triggerButton = filterRef.current?.querySelector('button');

      triggerButton?.click();
    },
  });

  useEffect(() => {
    if (options.length) {
      setTimeout(
        () =>
          setFilterOptions(
            options.map((f) => ({
              ...f,
              isChecked: activeOptions.some((_f) => _f.value === f.value),
            }))
          ),
        0
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- should be one time update on activeOptions
  }, [options]);

  useEffect(() => {
    if (filterOptions.length === 0) {
      setFilterOptions((prev) => [...prev, ...activeOptions]);

      return;
    }

    setFilterOptions((prev) =>
      prev.map((prevOption) => ({
        ...prevOption,
        isChecked: activeOptions.some(
          (option) => option.value === prevOption.value
        ),
      }))
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- want to exclude filter.length
  }, [activeOptions]);

  const handleDebouncedFilterSearch = useAsyncDebounce(
    handleFilterSearch,
    searchDebounce
  );

  const handleLocalFilterSearch = async <T extends string>(
    key: T,
    searchString: T
  ) => {
    if (method === ADVANCED_FILTERS_METHODS.SEARCH && !searchString) {
      setFilterOptions((prev) => prev.filter((option) => option.isChecked));
      setIsLoading(false);

      return;
    }

    setIsError(false);
    setFilterOptions((prev) => prev.filter((option) => option.isChecked));

    const responseOptions = await handleDebouncedFilterSearch(
      key,
      searchString
    );

    if (responseOptions === undefined) {
      setIsError(true);

      return;
    }

    if (responseOptions === false) {
      setFilterOptions([]);

      return;
    }

    const newOptions = responseOptions as MenuOption[];

    setFilterOptions((prev) => {
      const prevCheckedOptions = prev.filter((option) => option.isChecked);
      const optionsToExclude = new Set(
        prevCheckedOptions.map((option) => option.value)
      );
      const newVerifiedOptions = newOptions.filter(
        (option) => !optionsToExclude.has(option.value)
      );

      return [...prevCheckedOptions, ...newVerifiedOptions];
    });

    setIsLoading(false);
  };

  const optionsCountForSearch =
    method === ADVANCED_FILTERS_METHODS.FIXED
      ? Number(filterOptions.length) + 1
      : 0;

  const minOptionsForSearchValue =
    typeof minOptionsForSearch === 'number'
      ? minOptionsForSearch
      : optionsCountForSearch;

  const getNoDataText = () => {
    if (isError) {
      return filterSearchError ? l(filterSearchError) : '';
    }

    if (isLoading) {
      return l(loadingText);
    }

    if (method === ADVANCED_FILTERS_METHODS.SEARCH && !searchText) {
      return noDataText ? l(noDataText) : undefined;
    }

    return undefined;
  };

  return (
    <div ref={menuContainerRef}>
      <MenuMulti
        ref={filterRef}
        key={value}
        options={filterOptions}
        appendLabelsToButton
        label={l(label)}
        onChange={(selectedOptions) => {
          handleOptionsClick(value, method, selectedOptions ?? []);
        }}
        setIsExpanded={(isOpen) => {
          setIsMenuExpanded(isOpen);

          if (!isOpen) {
            setIsError(false);
          }
        }}
        trigger={<ButtonSelect kind='pill' label='' isDisabled={isDisabled} />}
        minOptionsForFooter={0}
        noDataText={
          method === ADVANCED_FILTERS_METHODS.INACTIVE
            ? 'WIP until API is integrated'
            : getNoDataText()
        }
        // shouldSortCheckedAtBottom={prioritizeCheckedOptions}
        showFooterLink1
        footerLink1Text={l(footerLink1Text)}
        footerLink2Text={l(footerLink2Text)}
        onFooterLink1Click={() => {
          onFooterLink1Click({
            value,
            isChecked: true,
            method,
          });
        }}
        onFooterLink2Click={() => {
          handleOptionsClick(value, method, []);
          // Check if search needs to be removed
          // handleLocalFilterSearch(value, '').catch(() => {});
        }}
        isFilteringDisabled
        minOptionsForSearch={minOptionsForSearchValue}
        onSearchTermChange={(searchString) => {
          setIsLoading(true);
          setSearchText(searchString);
          handleLocalFilterSearch(value, searchString).catch((err: unknown) => {
            createDevRejectionHandler('ChipFilter: handleLocalFilterSearch')(
              err
            );
          });
        }}
        showOptionLabelOnHover
      />
    </div>
  );
}

ChipFilter.defaultProps = {
  filterSearchError: '',
  enabledFilterValue: null,
  setEnabledFilterValue: () => {},
  isDisabled: false,
  minOptionsForSearch: undefined,
};

export default memo(ChipFilter);
